import { Button, Dropdown, Label } from '@heroui/react'
import { useQuery } from '@tanstack/react-query'
import { Link, useRouterState } from '@tanstack/react-router'
import { Compass, Heart, LayoutDashboard, LogIn, Menu, Trophy, UserPlus, Sparkles } from 'lucide-react'
import type { Key } from 'react'
import { useTranslations } from 'use-intl'
import { LocalizedLink } from '~/components/common/localized-link'
import { defaultLocale, type Locale } from '~/lib/i18n/shared'
import { createSessionQuery } from '~/lib/session-query'

interface PublicNavLinksProps {
  className?: string
  linkClassName?: string
}

interface PublicHeaderActionsProps {
  className?: string
}

function stripLocalePrefix(pathname: string) {
  return pathname.replace(/^\/(?:zh-CN|zh-TW|en|ja|ko)(?=\/|$)/, '') || '/'
}

function navLinkClass(baseClassName: string | undefined, active: boolean) {
  const base = baseClassName ?? 'hover:text-primary transition-colors'
  return active ? `${base} text-primary` : base
}

function localizedHref(path: string, locale: Locale | undefined) {
  if (!locale || locale === defaultLocale)
    return path
  return `/${locale}${path === '/' ? '' : path}`
}

export function PublicNavLinks({ className = 'hidden md:flex items-center gap-4 text-sm font-medium', linkClassName }: PublicNavLinksProps) {
  const t = useTranslations('components.layout.header')
  const pathname = useRouterState({ select: state => state.location.pathname })
  const { data } = useQuery(createSessionQuery())
  const isLoggedIn = !!data?.session
  const tier = data?.user?.tier ?? 'FREE'
  const isPaid = tier === 'EXPLORER' || tier === 'PRO' || tier === 'PREMIUM'
  const normalizedPath = stripLocalePrefix(pathname)

  return (
    <nav className={className} aria-label={t('brandName')}>
      <LocalizedLink to="/discovery" className={navLinkClass(linkClassName, normalizedPath.startsWith('/discovery'))}>
        {t('discovery')}
      </LocalizedLink>
      <LocalizedLink to="/rankings" className={navLinkClass(linkClassName, normalizedPath.startsWith('/rankings'))}>
        {t('rankings')}
      </LocalizedLink>
      {isLoggedIn && (
        <LocalizedLink to="/favorites" className={navLinkClass(linkClassName, normalizedPath.startsWith('/favorites'))}>
          {t('favorites')}
        </LocalizedLink>
      )}
      {isLoggedIn && isPaid && (
        <LocalizedLink to="/ai-assistant" className={navLinkClass(linkClassName, normalizedPath.startsWith('/ai-assistant'))}>
          <span className="flex items-center gap-1">
            <Sparkles className="w-4 h-4 text-primary" />
            {t('aiAssistant')}
          </span>
        </LocalizedLink>
      )}
    </nav>
  )
}

export function PublicHeaderActions({ className = 'flex items-center gap-3' }: PublicHeaderActionsProps) {
  const t = useTranslations('components.layout.header')
  const { data } = useQuery(createSessionQuery())
  const isLoggedIn = !!data?.session
  const isPremium = data?.user?.tier === 'PREMIUM'

  return (
    <div className={className}>
      {isLoggedIn
        ? (
            <>
              {!isPremium && (
                <LocalizedLink to="/pricing">
                  <Button size="sm" className="bg-amber-100 text-amber-700 hover:bg-amber-200 font-bold hidden sm:flex">
                    {t('upgradePremium') || '💎 Upgrade'}
                  </Button>
                </LocalizedLink>
              )}
              <LocalizedLink to="/dashboard">
                <Button size="sm">{t('dashboard')}</Button>
              </LocalizedLink>
            </>
          )
        : (
            <>
              <Link to="/auth/$pathname" params={{ pathname: 'sign-in' }}>
                <Button variant="ghost">{t('signIn')}</Button>
              </Link>
              <Link to="/auth/$pathname" params={{ pathname: 'sign-up' }}>
                <Button size="sm">{t('joinNow')}</Button>
              </Link>
            </>
          )}
    </div>
  )
}

export function MobilePublicNavMenu({ className = 'md:hidden' }: { className?: string }) {
  const t = useTranslations('components.layout.header')
  const routeParams = useRouterState({ select: state => state.matches.at(-1)?.params as Record<string, any> | undefined })
  const pathname = useRouterState({ select: state => state.location.pathname })
  const { data } = useQuery(createSessionQuery())
  const isLoggedIn = !!data?.session
  const tier = data?.user?.tier ?? 'FREE'
  const isPaid = tier === 'EXPLORER' || tier === 'PRO' || tier === 'PREMIUM'
  const locale = routeParams?.locale as Locale | undefined
  const normalizedPath = stripLocalePrefix(pathname)

  const handleAction = (key: Key) => {
    const destination = String(key)
    window.location.href = destination
  }

  return (
    <Dropdown>
      <Button
        variant="ghost"
        size="sm"
        isIconOnly
        aria-label="Open navigation menu"
        className={className}
      >
        <Menu className="h-5 w-5" />
      </Button>
      <Dropdown.Popover
        placement="bottom end"
        className="w-64 max-w-[calc(100vw-24px)] rounded-3xl p-2 shadow-2xl"
      >
        <Dropdown.Menu
          aria-label="Primary navigation"
          className="gap-1"
          onAction={handleAction}
          selectedKeys={new Set([normalizedPath.startsWith('/discovery') ? localizedHref('/discovery', locale) : normalizedPath.startsWith('/rankings') ? localizedHref('/rankings', locale) : normalizedPath.startsWith('/favorites') ? localizedHref('/favorites', locale) : normalizedPath.startsWith('/ai-assistant') ? localizedHref('/ai-assistant', locale) : ''])}
          selectionMode="single"
        >
          <Dropdown.Item key={localizedHref('/discovery', locale)} id={localizedHref('/discovery', locale)} textValue={t('discovery')} className="rounded-2xl px-3 py-2">
            <div className="flex min-h-11 items-center gap-4 text-base font-semibold">
              <Compass className="h-5 w-5 shrink-0 text-primary" />
              <Label className="leading-none">{t('discovery')}</Label>
            </div>
          </Dropdown.Item>
          <Dropdown.Item key={localizedHref('/rankings', locale)} id={localizedHref('/rankings', locale)} textValue={t('rankings')} className="rounded-2xl px-3 py-2">
            <div className="flex min-h-11 items-center gap-4 text-base font-semibold">
              <Trophy className="h-5 w-5 shrink-0 text-primary" />
              <Label className="leading-none">{t('rankings')}</Label>
            </div>
          </Dropdown.Item>
          <Dropdown.Item key={localizedHref('/favorites', locale)} id={localizedHref('/favorites', locale)} textValue={t('favorites')} className="rounded-2xl px-3 py-2">
            <div className="flex min-h-11 items-center gap-4 text-base font-semibold">
              <Heart className="h-5 w-5 shrink-0 text-primary" />
              <Label className="leading-none">{t('favorites')}</Label>
            </div>
          </Dropdown.Item>
          {isLoggedIn ? (
            <>
              <Dropdown.Item key="/dashboard" id="/dashboard" textValue={t('dashboard')} className="rounded-2xl px-3 py-2">
                <div className="flex min-h-11 items-center gap-4 text-base font-semibold">
                  <LayoutDashboard className="h-5 w-5 shrink-0 text-primary" />
                  <Label className="leading-none">{t('dashboard')}</Label>
                </div>
              </Dropdown.Item>
              {isPaid && (
                <Dropdown.Item key={localizedHref('/ai-assistant', locale)} id={localizedHref('/ai-assistant', locale)} textValue={t('aiAssistant')} className="rounded-2xl px-3 py-2">
                  <div className="flex min-h-11 items-center gap-4 text-base font-semibold">
                    <Sparkles className="h-5 w-5 shrink-0 text-primary" />
                    <Label className="leading-none">{t('aiAssistant')}</Label>
                  </div>
                </Dropdown.Item>
              )}
            </>
          ) : (
            <>
              <Dropdown.Item key="/auth/sign-in" id="/auth/sign-in" textValue={t('signIn')} className="rounded-2xl px-3 py-2">
                <div className="flex min-h-11 items-center gap-4 text-base font-semibold">
                  <LogIn className="h-5 w-5 shrink-0 text-primary" />
                  <Label className="leading-none">{t('signIn')}</Label>
                </div>
              </Dropdown.Item>
              <Dropdown.Item key="/auth/sign-up" id="/auth/sign-up" textValue={t('joinNow')} className="rounded-2xl px-3 py-2">
                <div className="flex min-h-11 items-center gap-4 text-base font-semibold">
                  <UserPlus className="h-5 w-5 shrink-0 text-primary" />
                  <Label className="leading-none">{t('joinNow')}</Label>
                </div>
              </Dropdown.Item>
            </>
          )}
        </Dropdown.Menu>
      </Dropdown.Popover>
    </Dropdown>
  )
}
